import axiosPrivate from 'config/axiosPrivate';
import React, { useEffect, useRef, useState } from 'react';
import * as yup from 'yup';

import {
  CustomAutocomplete,
  CustomAutocompleteFreeSolo,
} from 'components/shared/DropDown/CustomAutocomplete';
import { CustomChipsAutocomplete } from 'components/shared/DropDown/CustomChipsAutocomplete';
import { useHistory } from 'react-router-dom';

import QuitProjectCreationModal from 'components/shared/modal/QuitProjectCreationModal';
import { useTranslation } from 'react-i18next';
import { AssignChefProject } from 'components/AdminClient/AssignChefProjet';
import './CreateWebsite.css';
import usePageTracking from 'utils/hooks/usePageTracking';

const ADMIN_CLIENT_ADD_WEBSITE_PAGE = '/AdminClient/AjouterSiteWeb';
const COMPANY_INFO_MAX_SIZE = 10 * 1024 * 1024;
const COMPANY_INFO_ACCEPTED_EXTENSIONS = ['.pdf', '.doc', '.docx'];
const COMPANY_INFO_ACCEPT =
  '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const WEBSITE_COUNTRIES = [
  { label: 'France', code: 'FR' },
  { label: 'Espagne', code: 'ES' },
  { label: 'Suisse', code: 'CH' },
  { label: 'Belgique', code: 'BE' },
  { label: 'Allemagne', code: 'DE' },
  { label: 'Etats-Unis', code: 'US' },
  { label: 'Angleterre', code: 'UK' },
  { label: 'Italie', code: 'IT' },
  { label: 'Portugal', code: 'PT' },
];

const getLanguageCode = (lang) =>
  String(lang?.abbreviation || lang?.isoCode || lang?.code || '')
    .trim()
    .toLowerCase();

const findMatchingLanguage = (languagesList, rawLanguage) => {
  if (!rawLanguage || !languagesList?.length) return null;

  if (rawLanguage.id != null) {
    const byId = languagesList.find(
      (lang) => lang?.id != null && String(lang.id) === String(rawLanguage.id)
    );
    if (byId) return byId;
  }

  const rawCode = getLanguageCode(rawLanguage);
  if (rawCode) {
    const byCode = languagesList.find(
      (lang) => getLanguageCode(lang) === rawCode
    );
    if (byCode) return byCode;
  }

  if (rawLanguage.language) {
    const byName = languagesList.find(
      (lang) =>
        String(lang?.language || '').toLowerCase() ===
        String(rawLanguage.language).toLowerCase()
    );
    if (byName) return byName;
  }

  return null;
};

const normalizeCountryCode = (country) => {
  if (!country) return null;
  const raw =
    typeof country === 'string'
      ? country
      : country?.code || country?.isoCode || '';
  const code = String(raw).trim().toUpperCase();
  return code || null;
};

const API = {
  getAvailableThematics: '/project/thematic/all',
  getAvailableLanguages: '/project/language/all',
  pixopChefProjects: '/user/pixOpsChefProject',
  getAllConsultants: '/user/all',
  getAllChefProjects: '/user/chefProjects',
  createWebsite: '/project/manager/website',
  getProjectManager: '/user/external/project-managers',
  updateWebsite: '/project/manager/website',
  getCompanyInfoFileUrl: (websiteId) =>
    `/project/website/${websiteId}/company-info-file`,
};

const getFileExtension = (fileName = '') => {
  const lastDot = String(fileName).lastIndexOf('.');
  if (lastDot < 0) return '';
  return String(fileName).slice(lastDot).toLowerCase();
};

const getFileNameFromContentDisposition = (header = '') => {
  if (!header) return null;
  const utf8Match = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim().replace(/^["']|["']$/g, ''));
    } catch (e) {
      return utf8Match[1].trim().replace(/^["']|["']$/g, '');
    }
  }
  const plainMatch = /filename\s*=\s*([^;]+)/i.exec(header);
  if (plainMatch?.[1]) {
    return plainMatch[1].trim().replace(/^["']|["']$/g, '');
  }
  return null;
};

const getCompanyInfoFileNameFromBlob = (blob, headerFileName) => {
  if (headerFileName) return headerFileName;
  const type = String(blob?.type || '').toLowerCase();
  if (type.includes('pdf')) return 'company-info.pdf';
  if (type.includes('wordprocessingml')) return 'company-info.docx';
  if (type.includes('msword')) return 'company-info.doc';
  return 'company-info-file';
};

const buildWebsiteFormData = (managerWebsiteDto, companyInfoFile) => {
  const formData = new FormData();

  formData.append(
    'managerWebsiteDto',
    new Blob([JSON.stringify(managerWebsiteDto)], { type: 'application/json' })
  );
  if (companyInfoFile) {
    formData.append('companyInfoFile', companyInfoFile);
  }
  return formData;
};

const getMultipartRequestConfig = () => ({
  headers: {
    'Content-Type': false,
  },
});

const submitWebsiteRequest = (method, url, managerWebsiteDto, companyInfoFile) =>
  axiosPrivate[method](
    url,
    buildWebsiteFormData(managerWebsiteDto, companyInfoFile),
    getMultipartRequestConfig()
  );

export default function CreateWebsiteAdminChefProject() {
  usePageTracking(ADMIN_CLIENT_ADD_WEBSITE_PAGE);

  const history = useHistory();
  const { t } = useTranslation();

  const [pixOpData, setPixOpData] = useState([]);
  const [disabledForm, setDisabledForm] = useState(true);

  const [accountsList, setAccountsList] = useState([]);
  const [account, setAccount] = useState(null);

  const [urlSitesList, setUrlSitesList] = useState([]);
  const [urlSite, setUrlSite] = useState(null);
  const [websiteLanguage, setWebsiteLanguage] = useState(null);
  const isEditMode = Boolean(localStorage.getItem('editWebsiteId'));
  const [websiteCountry, setWebsiteCountry] = useState(
    isEditMode
      ? null
      : WEBSITE_COUNTRIES.find((country) => country.code === 'FR') || null
  );
  const [consultant, setConsultant] = useState(null);
  const [consultantsList, setConsultantsList] = useState([]);

  const [chefProjectsList, setChefProjectsList] = useState([]);

  const [availableThematicsList, setAvailableThematicsList] = useState([]);
  const [websiteThematics, setWebsiteThematics] = useState([]);
  const [websiteThematicsInput, setWebsiteThematicsInput] = useState('');


  const [availableLanguagesList, setAvailableLanguagesList] = useState([]);
  const [coconsTextNbr, setCoconsTextNbr] = useState(0);
  const [coconsNbr, setCoconsNbr] = useState(0);
  const [faqNbr, setFaqNbr] = useState(0);
  const [gmbNbr, setGmbNbr] = useState(0);
  const [textOptimizerNbr, setTextOptimizerNbr] = useState(0);

  const validationSchema = yup.object().shape({
    coconsTextNbr: yup.number().min(0).required(),
    coconsNbr: yup.number().min(0).required(),
    faqNbr: yup.number().min(0).required(),
    gmbNbr: yup.number().min(0).required(),
    textOptimizerNbr: yup.number().min(0).required(),
  });

  const [assignedChefProjects, setAssignedChefProjects] = useState([]);

  const [websiteSuccessModalOpen, setWebsiteSuccessModalOpen] = useState(false);
  const [websiteSuccessContent, setWebsiteSuccessContent] = useState({
    isCreate: false,
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [websiteErrorModalOpen, setWebsiteErrorModalOpen] = useState(false);
  const [websiteErrorModalContent, setWebsiteErrorModalContent] = useState({
    title: '',
    message: '',
    isUrlAlreadyExists: false,
    isFileTooLarge: false,
    isUnsupportedMediaType: false,
  });
  const [companyInfoFile, setCompanyInfoFile] = useState(null);
  const [existingCompanyInfoFile, setExistingCompanyInfoFile] = useState(null);
  const [companyInfoFileError, setCompanyInfoFileError] = useState('');
  const [isDraggingCompanyInfo, setIsDraggingCompanyInfo] = useState(false);
  const companyInfoFileInputRef = useRef(null);
  const existingCompanyInfoObjectUrlRef = useRef(null);

  const revokeExistingCompanyInfoObjectUrl = () => {
    if (existingCompanyInfoObjectUrlRef.current) {
      URL.revokeObjectURL(existingCompanyInfoObjectUrlRef.current);
      existingCompanyInfoObjectUrlRef.current = null;
    }
  };

  useEffect(() => () => revokeExistingCompanyInfoObjectUrl(), []);


  const openWebsiteErrorModal = (statusCode) => {
    if (statusCode === 406) {
      setWebsiteErrorModalContent({
        title: t('URL déjà existante'),
        message: t('Ce site est déjà enregistré dans votre workspace. Vous pouvez y accéder directement depuis votre dashboard.'),
        isUrlAlreadyExists: true,
        isFileTooLarge: false,
        isUnsupportedMediaType: false,
      });
    } else if (statusCode === 413) {
      const fileTooLargeMessage = t(
        'Le fichier Informations entreprise est trop volumineux. Veuillez choisir un fichier de 10 Mo maximum.'
      );
      setCompanyInfoFileError(t('Fichier trop volumineux (max 10 Mo)'));
      setWebsiteErrorModalContent({
        title: t('Fichier trop volumineux'),
        message: fileTooLargeMessage,
        isUrlAlreadyExists: false,
        isFileTooLarge: true,
        isUnsupportedMediaType: false,
      });
    } else if (statusCode === 415) {
      const unsupportedMessage = t(
        'Le format du fichier Informations entreprise n’est pas supporté. Formats acceptés : PDF ou Word.'
      );
      setCompanyInfoFileError(
        t('Format de fichier non supporté. Formats acceptés : PDF ou Word.')
      );
      setWebsiteErrorModalContent({
        title: t('Format de fichier non supporté'),
        message: unsupportedMessage,
        isUrlAlreadyExists: false,
        isFileTooLarge: false,
        isUnsupportedMediaType: true,
      });
    } else {
      setWebsiteErrorModalContent({
        title: t('Erreur inattendue'),
        message: t("Une erreur s'est produite lors de l'enregistrement. Notre équipe a été notifiée. Veuillez vérifier les informations de votre siteweb."),
        isUrlAlreadyExists: false,
        isFileTooLarge: false,
        isUnsupportedMediaType: false,
      });
    }
    setWebsiteErrorModalOpen(true);
  };

  useEffect(() => {
    document.title = "Typix - Créer un siteweb";
  }, []);
  useEffect(() => {
    window.dispatchEvent(new Event('locationchange'));
    axiosPrivate
      .get(API.getAvailableLanguages)
      .then((res) => setAvailableLanguagesList(res.data));

    axiosPrivate
      .get(API.getAvailableThematics)
      .then((res) => setAvailableThematicsList(res.data));

    axiosPrivate
      .get(API.getProjectManager)
      .then((res) => setChefProjectsList(res.data));
  }, []);

  useEffect(() => {
    const websiteId = localStorage.getItem('editWebsiteId');
    if (!websiteId) return;

    if (!availableLanguagesList?.length) return;

    const fetchWebsite = async () => {
      try {
        const { data } = await axiosPrivate.get(`${API.createWebsite}/${websiteId}`);

        if (data?.urlSite) setUrlSite(data.urlSite);

        if (data?.language) {
          const matchedLang = findMatchingLanguage(
            availableLanguagesList,
            data.language
          );
          setWebsiteLanguage(matchedLang || null);
        }

        if (data?.country) {
          const countryCode = normalizeCountryCode(data.country);
          const matchedCountry = WEBSITE_COUNTRIES.find(
            (country) => country.code === countryCode
          );
          if (matchedCountry) setWebsiteCountry(matchedCountry);
        }

        if (Array.isArray(data?.thematics)) {
          const matchedThematics = data.thematics.map((th) => {
            const found = (availableThematicsList || []).find(
              (t) => t?.id === th?.id || t?.thematic === th?.thematic || t === th
            );
            return found || th;
          });
          setWebsiteThematics(matchedThematics.filter(Boolean));
        }

        if (Array.isArray(data?.projectManagers) && data.projectManagers.length > 0) {
          const mapped = data.projectManagers.map((m) => ({
            id: m?.id ?? 0,
            name: m?.lastName || m?.name || '',
            firstName: m?.firstName || '',
            mail: m?.mail || '',
            phoneNumber: m?.phoneNumber || '',
            isoLanguageCode: m?.isoLanguageCode || '',
            isNew: false,
          }));
          setAssignedChefProjects(mapped);
        }

        try {
          const fileRes = await axiosPrivate.get(API.getCompanyInfoFileUrl(websiteId), {
            responseType: 'blob',
          });
          const blob = fileRes?.data;
          if (blob && blob.size > 0) {
            revokeExistingCompanyInfoObjectUrl();
            const objectUrl = URL.createObjectURL(blob);
            existingCompanyInfoObjectUrlRef.current = objectUrl;
            const headerFileName = getFileNameFromContentDisposition(
              fileRes.headers?.['content-disposition'] ||
              fileRes.headers?.['Content-Disposition']
            );
            setExistingCompanyInfoFile({
              url: objectUrl,
              name: getCompanyInfoFileNameFromBlob(blob, headerFileName),
            });
          } else {
            revokeExistingCompanyInfoObjectUrl();
            setExistingCompanyInfoFile(null);
          }
        } catch (fileError) {
          revokeExistingCompanyInfoObjectUrl();
          setExistingCompanyInfoFile(null);
        }
      } catch (e) {

      }
    };

    fetchWebsite();
  }, [availableLanguagesList, availableThematicsList]);

  useEffect(() => {
    const checkValidForm = () => {
      let invalid = false;
      try {
        validationSchema.validateSync({
          coconsTextNbr,
          coconsNbr,
          faqNbr,
          gmbNbr,
          textOptimizerNbr,
        });
      } catch (error) {
        invalid = true;
      }

      if (
        !urlSite ||
        !websiteLanguage ||
        !websiteCountry ||
        (websiteThematics.length === 0 &&
          !websiteThematicsInput?.trim()) ||
        invalid
      ) {
        setDisabledForm(true);
      } else {
        setDisabledForm(false);
      }
    };
    checkValidForm();
  }, [
    urlSite,
    websiteLanguage,
    websiteCountry,
    websiteThematics,
    websiteThematicsInput,
    validationSchema,
    coconsTextNbr,
    coconsNbr,
    faqNbr,
    gmbNbr,
    textOptimizerNbr,
  ]);

  const callbackFromMultiSelect = (newList) => {
    setWebsiteThematics(newList);
    // on vide la saisie libre une fois qu'une thématique est ajoutée en chip
    setWebsiteThematicsInput('');
  };

  const validateCompanyInfoFile = (file) => {
    if (!file) return t('Format de fichier non supporté');

    const extension = getFileExtension(file.name);
    if (!COMPANY_INFO_ACCEPTED_EXTENSIONS.includes(extension)) {
      return t('Format de fichier non supporté. Formats acceptés : PDF ou Word.');
    }

    if (file.size > COMPANY_INFO_MAX_SIZE) {
      return t('Fichier trop volumineux (max 10 Mo)');
    }

    return '';
  };

  const applyCompanyInfoFile = (file, limitExceeded = false) => {
    if (!file) return;
    const error = validateCompanyInfoFile(file);
    if (error) {
      setCompanyInfoFileError(error);
      return;
    }
    setCompanyInfoFileError(
      limitExceeded ? t('Vous pouvez importer 1 fichier maximum.') : ''
    );
    setCompanyInfoFile(file);
    revokeExistingCompanyInfoObjectUrl();
    setExistingCompanyInfoFile(null);
  };

  const importCompanyInfoFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    applyCompanyInfoFile(files[0], files.length > 1);
  };

  const handleCompanyInfoFileChange = (event) => {
    importCompanyInfoFiles(event.target.files);
    // allow re-selecting the same file
    event.target.value = '';
  };

  const handleCompanyInfoDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingCompanyInfo(false);
    if (companyInfoFile || existingCompanyInfoFile) {
      setCompanyInfoFileError(t('Vous pouvez importer 1 fichier maximum.'));
      return;
    }
    importCompanyInfoFiles(event.dataTransfer?.files);
  };

  const hasCompanyInfoFile = Boolean(companyInfoFile || existingCompanyInfoFile);

  const openCompanyInfoFilePicker = () => {
    if (hasCompanyInfoFile) {
      setCompanyInfoFileError(t('Vous pouvez importer 1 fichier maximum.'));
      return;
    }
    companyInfoFileInputRef.current?.click();
  };

  const removeCompanyInfoFile = () => {
    setCompanyInfoFile(null);
    revokeExistingCompanyInfoObjectUrl();
    setExistingCompanyInfoFile(null);
    setCompanyInfoFileError('');
    if (companyInfoFileInputRef.current) {
      companyInfoFileInputRef.current.value = '';
    }
  };

  const submitWebsite = () => {
    const editWebsiteId = localStorage.getItem('editWebsiteId');

    // on s'assure de prendre en compte une thématique saisie au clavier
    let finalThematics = websiteThematics;
    const trimmedInput = websiteThematicsInput.trim();
    if (trimmedInput) {
      const alreadyExists = websiteThematics.some(
        (th) =>
          (th.thematic || '').toLowerCase() === trimmedInput.toLowerCase()
      );
      if (!alreadyExists) {
        finalThematics = [
          ...websiteThematics,
          { id: 0, thematic: trimmedInput },
        ];
      }
    }

    const projectManagersPayload = assignedChefProjects
      .filter((manager) => manager.name && manager.mail)
      .map((manager) => {
        const pm = {
          firstName: manager.firstName,
          lastName: manager.name,
          name: manager.name,
          mail: manager.mail,
          phoneNumber: manager.phoneNumber,
        };
        if (manager.id) pm.id = manager.id;
        return pm;
      });

    if (editWebsiteId) {
      const managerWebsiteDto = {
        websiteDTO: {
          id: parseInt(editWebsiteId),
          urlSite: urlSite,
          numberOfProjects: 0,
          language: websiteLanguage,
          country: websiteCountry?.code,
          thematics: finalThematics,
        },
        projectManagers: projectManagersPayload,
      };

      setIsSubmitting(true);
      submitWebsiteRequest('put', API.updateWebsite, managerWebsiteDto, companyInfoFile)
        .then(() => {
          setWebsiteSuccessContent({
            isCreate: false,
            message: `${t('Le site web')} ${urlSite} ${t('a été modifié avec succès').replace(/!$/, '')}\u00A0!`,
          });
          setWebsiteSuccessModalOpen(true);
        })
        .catch((e) => {
          openWebsiteErrorModal(e.response?.status);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
      return;
    }

    const managerWebsiteDto = {
      websiteDTO: {
        urlSite: urlSite,
        language: websiteLanguage,
        country: websiteCountry?.code,
        thematics: finalThematics,
      },
      projectManagers: assignedChefProjects
        .filter((manager) => manager.name && manager.mail)
        .map((manager) => ({
          id: manager.id,
          firstName: manager.firstName,
          lastName: manager.name,
          mail: manager.mail,
          phoneNumber: manager.phoneNumber,
        })),
    };

    setIsSubmitting(true);
    submitWebsiteRequest('post', API.createWebsite, managerWebsiteDto, companyInfoFile)
      .then(() => {
        try {
          if (urlSite) {
            window.localStorage.setItem('AC_Dashboard_SelectedWebsite', urlSite);
            const eventName = 'websiteFilterChange_AC_Dashboard_SelectedWebsite';
            window.dispatchEvent(new CustomEvent(eventName, { detail: { value: urlSite } }));
          }
        } catch (e) {
        }
        setWebsiteSuccessContent({
          isCreate: true,
          message: `${t('Le site web a été créé avec succès').replace(/!$/, '')}\u00A0!`,
        });
        setWebsiteSuccessModalOpen(true);
      })
      .catch((e) => {
        openWebsiteErrorModal(e.response?.status);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const addChefProject = () => {
    setAssignedChefProjects([
      ...assignedChefProjects,
      {
        id: '',
        name: '',
        firstName: '',
        mail: '',
        phoneNumber: '',
        isoLanguageCode: '',
        isNew: false,
      },
    ]);
  };

  const updateChefProjectsList = (index, type, object) => {
    const newValue = assignedChefProjects[index];
    newValue[type] = object;
    const newList = assignedChefProjects;
    newList[index] = newValue;
    setAssignedChefProjects([...newList]);
  };

  const deleteChefProject = (index) => {
    let newList = assignedChefProjects;
    newList.splice(index, 1);
    setAssignedChefProjects([...newList]);
  };

  return (
    <div className="content-center create-website-page">
      {isSubmitting && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '22px 24px',
              maxWidth: '520px',
              width: '100%',
              boxShadow: '0 12px 30px rgba(15, 23, 42, 0.18)',
              textAlign: 'center',
            }}
          >
            <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '8px' }}>
              {localStorage.getItem('editWebsiteId')
                ? t('Mise à jour de votre site web en cours…')
                : t('Création siteweb en cours....')}
            </div>
            <p style={{ margin: 0, color: '#4b5563' }}>
              {t('Merci de patienter quelques instants. Nous finalisons la configuration de votre site.')}
            </p>
          </div>
        </div>
      )}
      <div className="box-header-top header-info">
        <span
          onClick={history.goBack}
          className="back-icon me-3"
          role="button"
          aria-label="Retour"
          style={{ cursor: 'pointer', width: '30px', height: '30px', marginTop: '-10px' }}
        />
        <h1>{localStorage.getItem('editWebsiteId') ? t('Modifier le site web') : t('Nouveau site web')}</h1>
      </div>
      <div className="row">
        <div className="col-12 col-md-10 box-screen">
          <div className="header-title">{t('Fiche du site web')} </div>
          <div className="box-wrapper">
            <div className="row">
              <div className="col-12 col-md-12">
                <div className="row row-mb">
                  <div className="col-12 col-md-4">
                    <div className="label-title">{t('Url du site')}*</div>

                    <CustomAutocompleteFreeSolo
                      list={urlSitesList}
                      value={urlSite}
                      placeholder="https://www.monsite.com"
                      getOptionSelected={(option) => option === urlSite}
                      onInputChange={(value) => {
                        setUrlSite(value);
                      }}
                      setValue={(option) => {
                        setUrlSite(option);
                      }}
                      getOptionLabel={(option) => {
                        return option;
                      }}
                    />
                    <div style={{ color: '#aaa', fontSize: '12px' }}>
                      {t("Entrez une URL complète avec le format https://mondomaine.com")}
                    </div>
                  </div>
                  <div className="col-12 col-md-2">
                    <div className="label-title">{t('Langue')}*</div>
                    <CustomAutocomplete
                      disabled={!availableLanguagesList?.length}
                      list={availableLanguagesList}
                      value={websiteLanguage}
                      placeholder={t('Selectionnez une langue')}
                      setValue={(option) => setWebsiteLanguage(option)}
                      groupBy={(option) => false}
                      isOptionEqualToValue={(option, value) => {
                        if (!option || !value) return option === value;
                        if (option.id != null && value.id != null) {
                          return String(option.id) === String(value.id);
                        }
                        const optionCode = getLanguageCode(option);
                        const valueCode = getLanguageCode(value);
                        return Boolean(optionCode) && optionCode === valueCode;
                      }}
                      getOptionDisabled={(option) => false}
                      getOptionLabel={(option) => {
                        return option?.language ? t(option.language) : '';
                      }}
                    />
                  </div>
                  <div className="col-12 col-md-2">
                    <div className="label-title">{t('Pays')}*</div>
                    <CustomAutocomplete
                      list={WEBSITE_COUNTRIES}
                      value={websiteCountry}
                      placeholder={t('Sélectionnez un pays')}
                      setValue={(option) => setWebsiteCountry(option)}
                      groupBy={() => false}
                      isOptionEqualToValue={(option, value) =>
                        option?.code === value?.code
                      }
                      getOptionDisabled={() => false}
                      getOptionLabel={(option) => t(option.label)}
                    />
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="label-title tooltip-wrapper">
                      {t('Thématiques d’expertise')}*

                      <span className=".tooltip-icon" style={{ cursor: "pointer", color: "#ef4444" }}>ⓘ</span>
                      <span className="tooltip-text">
                        {t('Ces thématiques aident l’agent à produire du contenu ciblé et pertinent pour votre audiance')}
                      </span>
                    </div>
                    {availableThematicsList && (
                      <CustomChipsAutocomplete
                        callbackFromMultiSelect={callbackFromMultiSelect}
                        value={websiteThematics}
                        disabled={!availableThematicsList}
                        list={availableThematicsList}
                        setValue={(option) => setWebsiteThematics(option)}
                        inputValue={websiteThematicsInput}
                        onInputChange={(value) =>
                          setWebsiteThematicsInput(value)
                        }
                        getOptionLabel={(option) => {
                          return t(option.thematic);
                        }}
                      />
                    )}
                    <div style={{ color: '#aaa', fontSize: '12px' }}>
                      {t('Cliquez sur Entrée pour confirmer — ex : SEO, E-commerce, Finance')}
                    </div>
                  </div>
                </div>

                <div className="company-info-section">
                  <div className="company-info-title">
                    {t("Informations & tone of voice de l'entreprise")}{' '}
                    <span className="company-info-title-optional">
                      ({t('optionnel')})
                    </span>
                  </div>
                  <div className="company-info-description">
                    {t(
                      "Ajoutez un document décrivant l'entreprise, sa ligne éditoriale et son tone of voice pour rendre la rédaction de contenu plus pertinente.\nFormats acceptés : PDF ou Word (max 10 Mo)."
                    )}
                  
                  </div>

                  <div
                    className={`company-info-dropzone${isDraggingCompanyInfo ? ' is-dragging' : ''}${hasCompanyInfoFile ? ' is-limit-reached' : ''}`}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!hasCompanyInfoFile) setIsDraggingCompanyInfo(true);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!hasCompanyInfoFile) setIsDraggingCompanyInfo(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsDraggingCompanyInfo(false);
                    }}
                    onDrop={handleCompanyInfoDrop}
                    onClick={openCompanyInfoFilePicker}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openCompanyInfoFilePicker();
                      }
                    }}
                  >
                    <input
                      ref={companyInfoFileInputRef}
                      type="file"
                      accept={COMPANY_INFO_ACCEPT}
                      disabled={hasCompanyInfoFile}
                      style={{ display: 'none' }}
                      onChange={handleCompanyInfoFileChange}
                    />
                    <div className="company-info-dropzone-inner">
                      <div className="company-info-dropzone-icon">
                        <img src="/Images/imp-icon.png" alt="" />
                      </div>
                      <div className="company-info-dropzone-text-main">
                        {isDraggingCompanyInfo
                          ? t('Déposez votre fichier ici')
                          : t('Glissez-déposez votre fichier')}
                      </div>
                      <div className="company-info-dropzone-text-sub">
                        {t('ou cliquez pour parcourir')}
                      </div>
                      <span
                        className={`company-info-dropzone-button${hasCompanyInfoFile ? ' is-disabled' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          openCompanyInfoFilePicker();
                        }}
                      >
                        {t('Parcourir les fichiers')}
                      </span>
                    </div>
                  </div>

                  {(companyInfoFile || existingCompanyInfoFile) && (
                    <div className="company-info-file-row">
                      {companyInfoFile ? (
                        <span className="company-info-file-name" title={companyInfoFile.name}>
                          {companyInfoFile.name}
                        </span>
                      ) : (
                        <a
                          className="company-info-file-name company-info-file-link"
                          href={existingCompanyInfoFile.url}
                          download={existingCompanyInfoFile.name}
                          rel="noopener noreferrer"
                          title={existingCompanyInfoFile.name}
                        >
                          {existingCompanyInfoFile.name}
                        </a>
                      )}
                      <button
                        type="button"
                        className="company-info-file-remove"
                        onClick={removeCompanyInfoFile}
                        aria-label={t('Supprimer le fichier')}
                        title={t('Supprimer le fichier')}
                      >
                        <img
                          src="/Images/close-iconnn.png"
                          alt=""
                          style={{ width: 14, height: 14 }}
                        />
                      </button>
                    </div>
                  )}

                  {companyInfoFileError ? (
                    <div className="company-info-file-error">{companyInfoFileError}</div>
                  ) : null}
                </div>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderTop: '1px solid #eee',
                  paddingTop: '1rem',
                  marginTop: '1rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <button
                      className="bt-submit-y"
                      onClick={addChefProject}
                      style={{
                        height: '32px',
                        width: '32px',
                        padding: '0',
                        fontSize: '20px',
                        fontWeight: '300',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: '1',
                      }}
                    >
                      +
                    </button>
                    <span>{t('Ajouter un chef de projet')}</span>
                    <span style={{ fontSize: '12px', fontWeight: 400, color: '#aaa', marginLeft: '5px' }}>
                      ({t('optionnel')})
                    </span>
                  </div>
                </div>
                <div>
                  <div style={{ marginTop: '12px' }}>
                    {assignedChefProjects.map((chefProject, index) => (
                      <AssignChefProject
                        key={index}
                        id={index}
                        list={chefProjectsList.filter(
                          (item) => !assignedChefProjects.some((item2) => item2.mail === item.mail)
                        )}
                        new={true}
                        data={chefProject}
                        addChefProject={addChefProject}
                        updateChefProjectsList={(type, object) => updateChefProjectsList(index, type, object)}
                        isDeleteable={true}
                        deleteChefProject={() => deleteChefProject(index)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <br />
            <span>* {t('Champs obligatoires')}</span>
          </div>

          <div className="box-button-bt create-website-submit-row">
            <div className="create-website-submit-inner">
              {disabledForm ? (
                <button className="bt-submit-disabled-y create-website-submit-button">
                  {localStorage.getItem('editWebsiteId') ? t('Modifier') : t('Créer le siteweb')}
                </button>
              ) : (
                <button
                  className="bt-submit-y create-website-submit-button"
                  onClick={submitWebsite}
                  disabled={isSubmitting}
                >
                  {localStorage.getItem('editWebsiteId') ? t('Modifier') : t('Créer le siteweb')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <QuitProjectCreationModal
        isOpen={websiteSuccessModalOpen}
        onClose={() => {
          setWebsiteSuccessModalOpen(false);
          history.goBack();
        }}
        variant="enrichment"
        title={
          websiteSuccessContent.isCreate
            ? t('Site web créé')
            : t('Site web modifié')
        }
        message={websiteSuccessContent.message}
        suggestion={t(
          'Nous configurons actuellement vos agents IA. Ils seront bientôt prêts à vous assister dans la génération de vos contenus.'
        )}
        showSuggestion={websiteSuccessContent.isCreate}
        primaryButtonLabel={
          websiteSuccessContent.isCreate
            ? t('Découvrir mes agents')
            : t('Fermer')
        }
        onPrimaryAction={() => {
          setWebsiteSuccessModalOpen(false);
          if (websiteSuccessContent.isCreate) {
            history.push('/AdminClient/AgentIAList');
          } else {
            history.goBack();
          }
        }}
        secondaryButtonLabel={t('Fermer')}
        showSecondaryButton={websiteSuccessContent.isCreate}
        onSecondaryAction={() => {
          setWebsiteSuccessModalOpen(false);
          history.goBack();
        }}
      />
      <QuitProjectCreationModal
        isOpen={websiteErrorModalOpen}
        onClose={() => setWebsiteErrorModalOpen(false)}
        title={websiteErrorModalContent.title}
        message={websiteErrorModalContent.message}
        suggestion={
          websiteErrorModalContent.isUrlAlreadyExists ||
            websiteErrorModalContent.isFileTooLarge ||
            websiteErrorModalContent.isUnsupportedMediaType
            ? ''
            : t('Vous pouvez réessayer immédiatement ou contacter notre support pour obtenir de l’aide.')
        }
        primaryButtonLabel={
          websiteErrorModalContent.isUrlAlreadyExists ||
            websiteErrorModalContent.isFileTooLarge ||
            websiteErrorModalContent.isUnsupportedMediaType
            ? t('Fermer')
            : t('Réessayer')
        }
        secondaryButtonLabel={t('Contacter le support')}
        showSuggestion={
          !websiteErrorModalContent.isUrlAlreadyExists &&
          !websiteErrorModalContent.isFileTooLarge &&
          !websiteErrorModalContent.isUnsupportedMediaType
        }
        showSecondaryButton={
          !websiteErrorModalContent.isUrlAlreadyExists &&
          !websiteErrorModalContent.isFileTooLarge &&
          !websiteErrorModalContent.isUnsupportedMediaType
        }
        onPrimaryAction={() => setWebsiteErrorModalOpen(false)}
        onSecondaryAction={() => {
          setWebsiteErrorModalOpen(false);
          history.push('/AdminClient/Support');
        }}
      />
    </div>
  );
}
